import { z } from 'zod';

/**
 * Zod schemas for the MoonHub.API v2-auth (social login) surface
 * (`/v2/auth/*`). Mirrors MoonHub's app/types/oauth.ts so InOut.FE speaks the
 * exact same contract. Identity hashes / addresses are kept as plain strings
 * (the backend is the source of truth) to avoid over-strict FE rejection.
 */

export const oauthProviderSchema = z.enum([
    'google',
    'discord',
    'x',
    'telegram',
]);
export const bindingStateSchema = z.enum(['bound', 'unbound', 'unknown']);
export const rebindStateSchema = z.enum([
    'bound',
    'unbound',
    'rebound',
    'unchanged',
]);

/** POST /v2/auth/login -> the provider authorize URL to redirect to. */
export const loginResponseSchema = z.object({
    authorizeUrl: z.string().url(),
});

/** POST /v2/auth/callback -> session established, binding state reported. */
export const callbackResponseSchema = z.object({
    ok: z.literal(true),
    provider: oauthProviderSchema,
    needsBinding: z.boolean(),
    bindingState: bindingStateSchema,
    accountHash: z.string(),
    displayName: z.string().nullish(),
    username: z.string().nullish(),
});

/**
 * The authorization wallets this account already owns, keyed by anchor chain id.
 * Derived server-side from `custodial_wallet_registry`, so it is a HINT for the
 * anchor selector ("you already have a wallet here"), never an authority: the
 * chain is still read before anything is signed. Absent on an older backend.
 */
export const anchorWalletsSchema = z
    .object({
        '25': z.string().nullable(),
        '4663': z.string().nullable(),
    })
    .partial()
    .nullish();

/** GET /v2/auth/me -> current session (authenticated:false when logged out). */
export const meResponseSchema = z.object({
    authenticated: z.boolean(),
    anchors: anchorWalletsSchema,
    provider: oauthProviderSchema.nullish(),
    accountHash: z.string().nullish(),
    bindingState: bindingStateSchema.nullish(),
    custodialWalletAddress: z.string().nullish(),
    // the owner EOA this login is bound to. carried in the session since login; it was simply never
    // returned, so nothing could show which wallet a social account is attached to.
    owner: z.string().nullish(),
    uid: z.number().nullish(),
    oid: z.string().nullish(),
    displayName: z.string().nullish(),
    username: z.string().nullish(),
});

/** POST /v2/auth/rebind -> re-checks the on-chain custodial wallet binding. */
export const rebindResponseSchema = z.object({
    ok: z.literal(true),
    changed: z.boolean(),
    state: rebindStateSchema,
    bindingState: bindingStateSchema,
});

/** POST /v2/auth/logout. */
export const logoutResponseSchema = z.object({
    ok: z.literal(true),
});
